import * as React from "react"
import { cva } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Circle, Clock, Info, XCircle } from "lucide-react";

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-xs font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary-dark",
        secondary:
          "border-transparent bg-surface-hover text-foreground hover:bg-surface-hover",
        destructive:
          "border-transparent bg-danger-dark text-destructive-foreground hover:bg-danger-dark",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// State is never conveyed by colour alone: tone classes get a matching icon.
const TONES = [
  [/\b(badge-exception|badge-rejected)\b|(text|bg)-(red|rose|pink|danger|destructive)\b|(text|bg)-(red|rose|pink)-\d|\bbg-danger/, XCircle],
  [/\b(badge-qa|badge-pending)\b|(text|bg)-(amber|yellow|orange|warning)\b|(text|bg)-(amber|yellow|orange)-\d|\bbg-warning/, Clock],
  [/\b(badge-approved|badge-filed)\b|(text|bg)-(green|emerald|teal|lime|success)\b|(text|bg)-(green|emerald|teal|lime)-\d|\bbg-success/, CheckCircle2],
  [/\bbadge-matching\b|(text|bg)-(blue|sky|indigo|cyan|purple|violet|info)-\d|\btext-primary\b/, Info],
  [/\bbadge-(intake|closed|archived)\b|\bbg-(slate|gray)-(50|100)\b/, Circle],
];
const STATUS_ICONS = { success: CheckCircle2, warning: Clock, danger: XCircle, info: Info, neutral: Circle };
const toneIcon = (className, variant, status) => {
  if (status) return STATUS_ICONS[status] || Circle;
  if (variant === "destructive") return AlertTriangle;
  const hit = TONES.find(([re]) => re.test(className || ""));
  return hit ? hit[1] : null;
};
const isPlain = (children) => React.Children.toArray(children).every((c) => typeof c === "string" || typeof c === "number");

function Badge({
  className,
  variant,
  children,
  noIcon = false,
  status,
  ...props
}) {
  const Icon = !noIcon && isPlain(children) ? toneIcon(className, variant, status) : null;
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden="true" data-badge-icon="" />}
      {children}
    </div>
  );
}

export { Badge, badgeVariants }
